import { UNDEFINED_VALUE } from 'const/formatting'
import { BUBBLE, PIE } from 'const/widgets'
import { formatTooltip } from 'helpers/charts'
import { formatNumber } from 'helpers/formatting'
import { shortNameToNode } from 'helpers/typeGuard'
import { elementToString } from 'helpers/utilities'
import { transformToString } from 'helpers/widgets'
import { FieldMapping, Widget } from 'types'

const INDICATOR_FALLBACK_COLOR = '#000'

interface PointType {
    x: number
    y?: number
    z?: number
    tooltipExtra?: string
    name?: string
    color?: string
    percentage?: number
    isPriorPeriod?: boolean
    series?: { name?: string }
}

interface chartTooltipFormatterParams {
    point: PointType
    widget: Widget
    metricsConfig: FieldMapping
}

const bubbleChartTooltipFormatter = ({
    point,
    widget,
    metricsConfig,
}: chartTooltipFormatterParams): string => {
    const { x, y, z, tooltipExtra } = point

    const widgetMetrics = transformToString(widget.metrics)
    const metric = widgetMetrics[0]
    const metricConfig = metricsConfig[metric]

    const indicator = `<span class="indicator" style="background: ${
        point.color ?? INDICATOR_FALLBACK_COLOR
    };"></span>`
    const title = `<h3 style="display: flex; align-items: center">${indicator}${point.name}</h3>`

    const tooltipData = [
        {
            label: elementToString(
                shortNameToNode(metricConfig?.shortName, true)
            ),
            value: formatNumber(y, metricConfig?.metricOptions?.format),
        },
    ]
    if (widgetMetrics.length > 1) {
        tooltipData.push({
            label: elementToString(
                shortNameToNode(
                    metricsConfig[widgetMetrics[1]]?.shortName,
                    true
                )
            ),
            value: formatNumber(
                x,
                metricsConfig[widgetMetrics[1]]?.metricOptions?.format
            ),
        })
    }
    if (widgetMetrics.length > 2) {
        tooltipData.push({
            label: elementToString(
                shortNameToNode(
                    metricsConfig[widgetMetrics[2]]?.shortName,
                    true
                )
            ),
            value: formatNumber(
                z,
                metricsConfig[widgetMetrics[2]]?.metricOptions?.format
            ),
        })
    }
    const tooltip = tooltipData
        .map((record) =>
            formatTooltip(
                '#000000',
                record.label,
                record.value,
                undefined,
                false
            )
        )
        .join(' ')

    return `
        <div>
            ${title}
            <div class="points">${tooltip}</div>
            ${tooltipExtra ?? ''}
        </div>
    `
}

const categoricalChartTooltipFormatter = ({
    point,
    widget,
    metricsConfig,
}: chartTooltipFormatterParams): string => {
    const { y, tooltipExtra } = point
    const widgetMetrics = transformToString(widget.metrics)
    const metric = widgetMetrics[0]
    const metricConfig = metricsConfig[metric]
    const countTypes = ['count', 'currencyAmount']
    const showPercent =
        metricConfig?.metricOptions?.type &&
        countTypes.includes(metricConfig.metricOptions.type)

    const getPercentage = (
        percentage: number | undefined
    ): string | undefined => {
        if (percentage === undefined) {
            return undefined
        }
        if ((!widget.chart_type || widget.chart_type === PIE) && showPercent) {
            return formatNumber(percentage / 100, '0%')
        }
        return undefined
    }

    const isPriorPeriod = point.isPriorPeriod === true
    const headerLabel = elementToString(
        shortNameToNode(metricConfig?.shortName, true)
    )
    const header = isPriorPeriod ? `${headerLabel} (Prior Period)` : headerLabel

    const tooltip = formatTooltip(
        point.color ?? INDICATOR_FALLBACK_COLOR,
        point.name,
        formatNumber(y, metricConfig?.metricOptions?.format),
        getPercentage(point.percentage)
    )
    return `
            <h3>${header}</h3>
            <div class="points">${tooltip}</div>
            ${tooltipExtra ?? ''}
        `
}

export const chartTooltipFormatter = (
    params: chartTooltipFormatterParams
): string => {
    if ((params.widget.chart_type as string) === BUBBLE) {
        return bubbleChartTooltipFormatter(params)
    }
    return categoricalChartTooltipFormatter(params)
}

interface SharedPointType {
    y?: number
    color?: string
    series: { name?: string; options?: { isPriorPeriod?: boolean } }
    point: PointType
}

interface SharedCategoryTooltipParams {
    points: SharedPointType[]
    widget: Widget
    metricsConfig: FieldMapping
}

export const sharedCategoryTooltipFormatter = ({
    points,
    widget,
    metricsConfig,
}: SharedCategoryTooltipParams): string => {
    if (!points.length) {
        return ''
    }

    const widgetMetrics = transformToString(widget.metrics)
    const metric = widgetMetrics[0]
    const metricConfig = metricsConfig[metric]
    const format = metricConfig?.metricOptions?.format
    const categoryName = points[0].point.name ?? ''

    const currentPoint = points.find((p) => !p.series.options?.isPriorPeriod)
    const priorPoint = points.find((p) => p.series.options?.isPriorPeriod)

    const currentValue =
        currentPoint?.y != null
            ? formatNumber(currentPoint.y, format)
            : UNDEFINED_VALUE
    const priorValue =
        priorPoint?.y != null
            ? formatNumber(priorPoint.y, format)
            : UNDEFINED_VALUE

    const currentColor = currentPoint?.color ?? INDICATOR_FALLBACK_COLOR
    const currentIndicator = `<span class="indicator" style="background: ${currentColor};"></span>`
    const priorIndicator = `<span class="indicator" style="background: #DFDFDF;"></span>`

    const metricLabel = elementToString(
        shortNameToNode(metricConfig?.shortName, true)
    )

    const tooltipExtra = currentPoint?.point.tooltipExtra ?? ''

    return `
        <div>
            <h3>${categoryName}</h3>
            <table class="points">
                <tr>
                    <td class="point-label">${currentIndicator} ${metricLabel}:</td>
                    <td class="point-value">${currentValue}</td>
                </tr>
                <tr>
                    <td class="point-label">${priorIndicator} Prior Period:</td>
                    <td class="point-value">${priorValue}</td>
                </tr>
            </table>
            ${tooltipExtra}
        </div>
    `
}
